import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { HubTabs } from '@/types/enums';

/**
 * Core preferences ported from mobile-app/src/store/core.store.ts.
 * Drops the AsyncStorage thunks — persistence happens through `lib/storage`
 * (localStorage today, optionally `cloudStorage` later for cross-device sync).
 */
export interface CoreState {
  defaultGame: number;
  selectedGames: number[];
  activeGames: number[];
  selectedTab: HubTabs;
  currentSegments: string[];
  isCompactView: boolean;
  /** Hub coach-marks overlay on/off (started by onboarding Done or Settings). */
  tourActive: boolean;
  /** Hub section the user last selected. Survives leaving the Hub for a detail route (e.g. a battle). Session-scoped: a relaunch starts from the default section again. */
  hubSection: string | null;
  /** Battles panel segment (list vs stats). Session-scoped, same reasoning as hubSection. */
  battlesView: 'list' | 'stats';
  /** Battles list result filter chip. Plain string so the slice imports no component types. */
  battlesResultFilter: string;
  /** Battles list mode filter chip. Plain string so the slice imports no component types. */
  battlesModeFilter: string;
}

const initialState: CoreState = {
  defaultGame: 0,
  selectedGames: [],
  activeGames: [],
  selectedTab: HubTabs.MIX_GAMES,
  currentSegments: [],
  isCompactView: false,
  tourActive: false,
  hubSection: null,
  battlesView: 'list',
  battlesResultFilter: 'all',
  battlesModeFilter: 'all',
};

const coreSlice = createSlice({
  name: 'core',
  initialState,
  reducers: {
    set: (_state, action: PayloadAction<CoreState>) => action.payload,
    setDefaultGame: (state, action: PayloadAction<number>) => {
      state.defaultGame = action.payload;
    },
    setSelectedGames: (state, action: PayloadAction<number[]>) => {
      state.selectedGames = action.payload;
    },
    setActiveGames: (state, action: PayloadAction<number[]>) => {
      state.activeGames = action.payload;
    },
    setSelectedTab: (state, action: PayloadAction<HubTabs>) => {
      state.selectedTab = action.payload;
    },
    setCurrentSegments: (state, action: PayloadAction<string[]>) => {
      state.currentSegments = action.payload;
    },
    setCompactView: (state, action: PayloadAction<boolean>) => {
      state.isCompactView = action.payload;
    },
    setTourActive(state, action: PayloadAction<boolean>) {
      state.tourActive = action.payload;
    },
    setHubSection(state, action: PayloadAction<string | null>) {
      state.hubSection = action.payload;
    },
    setBattlesView(state, action: PayloadAction<'list' | 'stats'>) {
      state.battlesView = action.payload;
    },
    setBattlesResultFilter(state, action: PayloadAction<string>) {
      state.battlesResultFilter = action.payload;
    },
    setBattlesModeFilter(state, action: PayloadAction<string>) {
      state.battlesModeFilter = action.payload;
    },
    clear: () => initialState,
  },
});

export const {
  set: setCore,
  setDefaultGame,
  setSelectedGames,
  setActiveGames,
  setSelectedTab,
  setCurrentSegments,
  setCompactView,
  setTourActive,
  setHubSection,
  setBattlesView,
  setBattlesResultFilter,
  setBattlesModeFilter,
  clear: clearCore,
} = coreSlice.actions;

export default coreSlice.reducer;
